import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Droplets, Scissors, Palette, Wind, ChevronRight, CheckCircle2, Fingerprint, Clock, Ticket, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { useBranding } from "@/context/BrandingContext";

const KIND_ICON = { "Wash & Rinse": Droplets, "Cut": Scissors, "Colour & Process": Palette, "Blow-dry & Style": Wind };

// Salon "one visit, many chairs → one ticket" view — real journeys from the backend,
// including no-charge ancillary (wash/processing) steps and an under-rang leakage case.
export default function AppointmentSession({ storeId }) {
  const { money, branding } = useBranding();
  const brand = branding.primary_color;
  const [idx, setIdx] = useState(0);

  const { data } = useQuery({
    queryKey: ["salon-sessions", storeId],
    queryFn: () => api.get(`/stores/${storeId}/sessions`).then((r) => r.data),
    enabled: !!storeId,
  });

  const sessions = data?.sessions || [];
  if (!sessions.length) return null;
  const s = sessions[Math.min(idx, sessions.length - 1)];

  return (
    <div data-testid="appointment-session" className="mt-8 surface-card p-6 animate-in fade-in slide-in-from-bottom-2 duration-500">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="font-display text-lg font-medium">Appointment Session</h2>
            <span className="rounded-full border border-[#2A3550] bg-[#1E2638] px-2 py-0.5 text-[10px] uppercase tracking-wider text-slate-400">Live data</span>
          </div>
          <p className="text-xs text-slate-400 mt-1">One visit across many chairs — wash to style — stitched into a single ticket.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span data-testid="session-signature" className="inline-flex items-center gap-1.5 rounded-full border border-[#2A3550] bg-[#171E2E] px-3 py-1.5 text-xs text-slate-300" title="Weight signature (illustrative) that stitches the chairs to one occupant">
            <Fingerprint className="h-3.5 w-3.5" style={{ color: brand }} /> Weight signature · {s.weight_kg}&nbsp;kg
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-[#2A3550] bg-[#171E2E] px-3 py-1.5 text-xs text-slate-300"><Clock className="h-3.5 w-3.5" /> ~{s.mins} min</span>
        </div>
      </div>

      {/* Journey selector */}
      {sessions.length > 1 && (
        <div className="mt-4 inline-flex rounded-lg border border-[#2A3550] bg-[#1E2638] p-0.5">
          {sessions.map((ss, i) => (
            <button key={ss.id} data-testid={`session-tab-${i}`} onClick={() => setIdx(i)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md transition-colors duration-200 ${i === idx ? "bg-[#38BDF8]/15 text-[#38BDF8]" : "text-slate-400 hover:text-white"}`}>
              {ss.leakage ? <AlertTriangle className="h-3.5 w-3.5" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
              {ss.leakage ? "Under-rang" : "Reconciled"}
            </button>
          ))}
        </div>
      )}

      <div className="mt-6 flex items-stretch gap-2 overflow-x-auto pb-2">
        {s.stations.map((st, i) => {
          const Icon = KIND_ICON[st.kind] || Scissors;
          const anc = st.role === "ancillary";
          const unbilled = st.role === "revenue" && !st.billed;
          const accent = anc ? "#94A3B8" : unbilled ? "#EF4444" : brand;
          return (
            <React.Fragment key={`${st.label}-${i}`}>
              <div data-testid={`session-step-${i}`} className="min-w-[152px] flex-1 rounded-xl border bg-[#171E2E] p-4"
                style={{ borderColor: unbilled ? "rgba(239,68,68,0.4)" : anc ? "#2A3550" : "rgba(56,189,248,0.35)" }}>
                <div className="flex items-center justify-between">
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg" style={{ background: anc ? "#1E2638" : unbilled ? "rgba(239,68,68,0.12)" : "rgba(56,189,248,0.12)", color: accent }}>
                    <Icon className="h-[18px] w-[18px]" />
                  </span>
                  <span className="rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider" style={{ background: anc ? "#1E2638" : unbilled ? "rgba(239,68,68,0.12)" : "rgba(56,189,248,0.12)", color: accent }}>
                    {anc ? "Ancillary" : unbilled ? "Unbilled" : "Revenue"}
                  </span>
                </div>
                <div className="mt-3 text-sm font-medium text-white">{st.kind}</div>
                <div className="text-xs text-slate-400">{st.label} · {st.mins} min</div>
                <div className="mt-2 text-sm font-display" style={{ color: unbilled ? "#F87171" : anc ? "#64748B" : "#fff" }}>
                  {anc ? "no charge" : unbilled ? `${money(st.price)} · not rung` : money(st.price)}
                </div>
              </div>
              {i < s.stations.length - 1 && <div className="flex items-center text-slate-600"><ChevronRight className="h-5 w-5" /></div>}
            </React.Fragment>
          );
        })}
      </div>

      {s.leakage ? (
        <div className="mt-4 flex items-center justify-between flex-wrap gap-3 rounded-xl border border-red-500/25 bg-red-500/[0.06] px-5 py-4" data-testid="session-leakage">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-red-400" />
            <div>
              <div className="text-sm font-medium text-white flex items-center gap-1.5"><Ticket className="h-4 w-4 text-red-400" /> Ticket {s.ticket_id} under-rang</div>
              <div className="text-xs text-slate-400">The colour service was delivered but never rung — the stitching catches what a per-chair view would miss.</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[11px] uppercase tracking-wider text-slate-400">Recovered leakage</div>
            <div className="font-display text-2xl text-red-400">{money(s.unbilled_total)}</div>
          </div>
        </div>
      ) : (
        <div className="mt-4 flex items-center justify-between flex-wrap gap-3 rounded-xl border border-emerald-500/25 bg-emerald-500/[0.06] px-5 py-4" data-testid="session-reconciled">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="h-5 w-5 text-emerald-400" />
            <div>
              <div className="text-sm font-medium text-white flex items-center gap-1.5"><Ticket className="h-4 w-4 text-emerald-400" /> Reconciled to one sale · {s.ticket_id}</div>
              <div className="text-xs text-slate-400">Counted as <strong className="text-slate-300">one visit across {s.chairs} chairs</strong> (wash is no-charge), not {s.chairs} separate services.</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[11px] uppercase tracking-wider text-slate-400">Ticket total</div>
            <div className="font-display text-2xl" style={{ color: brand }}>{money(s.ticket_total)}</div>
          </div>
        </div>
      )}
    </div>
  );
}
